<script setup>
// [TODO] This componet is too big and bulky to be in the same file, we can consider splitting this into multiple
// composables and components, useVirtualChatList, useChatlistFilters
import {
  ref,
  unref,
  provide,
  computed,
  watch,
  onMounted,
  defineEmits,
} from 'vue';
import { useStore } from 'vuex';
import { useRoute, useRouter } from 'vue-router';
import {
  useMapGetter,
  useFunctionGetter,
} from 'dashboard/composables/store.js';

import { Virtualizer } from 'virtua/vue';
import ChatListHeader from './ChatListHeader.vue';
import { usePinnedConversations } from 'dashboard/composables/usePinnedConversations';
import ConversationLabelQuickFilter from './ConversationLabelQuickFilter.vue';
import Dialog from 'dashboard/components-next/dialog/Dialog.vue';
import ConversationFilter from 'next/filter/ConversationFilter.vue';
import QuickFilterTabs from './widgets/QuickFilterTabs.vue';
import {
  QUICK_FILTERS,
  PRIMARY_SEGMENT_KEYS,
  FILTER_STATUS_CHOICES,
  FOLDER_FILTER_PREFIX,
  ADMIN_ASSIGNEE_SCOPE,
  ADMIN_ASSIGNEE_SCOPE_VALUES,
  isFolderFilterKey,
  isKanbanPresetKey,
  getQuickFilterNavigationTarget,
  findQuickFilterByKey,
  getConversationTypeForRequest,
  getInboxIdForRequest,
  normalizeConversationsQuickFilters,
  conversationsQuickFiltersStorageKey,
  readConversationsQuickFiltersRaw,
  sanitizeGlobalInboxIds,
  SITEUP_BUILTIN_SHORTCUT_DEFAULTS_VERSION,
  getAssigneeTypeForQuickFilter,
  getCountKeyForQuickFilter,
  conversationMatchesAssigneeFilter,
  conversationMatchesAdminAssigneeScope,
  normalizeIncludeUnassignedInPendentes,
  resolveQuickFilterBadgeCount,
  aggregateKanbanBadgeCounts,
} from './widgets/conversation/conversationQuickFilters.js';
import ConversationItem from './ConversationItem.vue';
import ConversationApi from 'dashboard/api/inbox/conversation';
import { debounce } from '@chatwoot/utils';
import DeleteCustomViews from 'dashboard/routes/dashboard/customviews/DeleteCustomViews.vue';
import ConversationBulkActions from './widgets/conversation/conversationBulkActions/Index.vue';
import TeleportWithDirection from 'dashboard/components-next/TeleportWithDirection.vue';
import Spinner from 'dashboard/components-next/spinner/Spinner.vue';
import Button from 'dashboard/components-next/button/Button.vue';
import PanelEmptyState from 'dashboard/components-next/panel/PanelEmptyState.vue';
import IntersectionObserver from 'dashboard/components/IntersectionObserver.vue';
import ConversationResolveAttributesModal from 'dashboard/components-next/ConversationWorkflow/ConversationResolveAttributesModal.vue';

import { useUISettings } from 'dashboard/composables/useUISettings';
import { useWorkbenchLayout } from 'dashboard/composables/useWorkbenchLayout';
import { useFocusMode } from 'dashboard/composables/useFocusMode';
import { bindFocusConversationQueue } from 'dashboard/composables/useFocusConversationQueue';
import { useKeyboardEvents } from 'dashboard/composables/useKeyboardEvents';
import { useAlert } from 'dashboard/composables';
import { useChatListKeyboardEvents } from 'dashboard/composables/chatlist/useChatListKeyboardEvents';
import { useBulkActions } from 'dashboard/composables/chatlist/useBulkActions';
import { useFilter } from 'shared/composables/useFilter';
import { useTrack } from 'dashboard/composables';
import { useI18n } from 'vue-i18n';
import {
  useCamelCase,
  useSnakeCase,
} from 'dashboard/composables/useTransformKeys';
import { useEmitter } from 'dashboard/composables/emitter';
import { useConversationRequiredAttributes } from 'dashboard/composables/useConversationRequiredAttributes';

import { emitter } from 'shared/helpers/mitt';

import wootConstants from 'dashboard/constants/globals';
import advancedFilterOptions from './widgets/conversation/advancedFilterItems';
import filterQueryGenerator, {
  normalizeSavedFilterQuery,
} from '../helper/filterQueryGenerator.js';
import languages from 'dashboard/components/widgets/conversation/advancedFilterItems/languages';
import countries from 'shared/constants/countries';
import { generateValuesForEditCustomViews } from 'dashboard/helper/customViewsHelper';
import { resolveSavedIconClass } from 'dashboard/helper/legacyIconClass';
import { conversationListPageURL } from '../helper/URLHelper';
import {
  isOnMentionsView,
  isOnParticipatingView,
  isOnUnattendedView,
} from '../store/modules/conversations/helpers/actionHelpers';
import {
  getUserPermissions,
  getUserRole,
  filterItemsByPermission,
} from 'dashboard/helper/permissionsHelper.js';
import { matchesFilters } from '../store/modules/conversations/helpers/filterHelpers';
import {
  conversationViewComparator,
  CONVERSATION_VIEW_MODE_SORT_KEYS,
} from '../store/modules/conversations/helpers';
import { CONVERSATION_EVENTS } from '../helper/AnalyticsHelper/events';
import {
  ASSIGNEE_TYPE_TAB_PERMISSIONS,
  ROLES,
} from 'dashboard/constants/permissions.js';

const props = defineProps({
  conversationInbox: { type: [String, Number], default: 0 },
  teamId: { type: [String, Number], default: 0 },
  label: { type: String, default: '' },
  conversationType: { type: String, default: '' },
  foldersId: { type: [String, Number], default: 0 },
  showConversationList: { default: true, type: Boolean },
  isOnExpandedLayout: { default: false, type: Boolean },
});

const emit = defineEmits(['conversationLoad']);

// Adicionar KANBAN ao ASSIGNEE_TYPE
wootConstants.ASSIGNEE_TYPE.KANBAN = 'kanban';

const { uiSettings, updateUISettings } = useUISettings();
const {
  isListCompact,
  isAvatarRail,
  listDragWidth,
  toggleListCompact,
  startListDrag,
} = useWorkbenchLayout();
provide('isWorkbenchAvatarRail', isAvatarRail);
const { isFocusModeEnabled } = useFocusMode();
const { t } = useI18n();

useKeyboardEvents({
  'Alt+BracketLeft': { action: toggleListCompact },
});
const router = useRouter();
const route = useRoute();
const store = useStore();

const resolveAttributesModalRef = ref(null);
const conversationListRef = ref(null);
const virtualListRef = ref(null);
// SiteUp v647: personalizador de filtros rápidos (ref para openCustomizer).
const desktopQuickFilterTabsRef = ref(null);
// O header V3 tem os ícones, mas o ESTADO dos menus mora no QuickFilterTabs.
// Ler pelo ref é reativo porque o expose devolve os próprios refs/computeds.
const isChromeFilterOpen = computed(
  () => desktopQuickFilterTabsRef.value?.isFilterOpen ?? false
);
const isChromeShortcutsOpen = computed(
  () => desktopQuickFilterTabsRef.value?.isMoreOpen ?? false
);
const isChromeFilterActive = computed(
  () => desktopQuickFilterTabsRef.value?.hasAppliedFilterScope ?? false
);

provide('contextMenuElementTarget', virtualListRef);

const activeAssigneeTab = ref(wootConstants.ASSIGNEE_TYPE.ME);
const activeStatus = ref(wootConstants.STATUS_TYPE.OPEN);
// SiteUp: tipo de conversa aplicado pelos filtros rápidos (unread/scheduled)
const activeConversationType = ref('');
const activeSortBy = ref(wootConstants.SORT_BY_TYPE.LAST_ACTIVITY_AT_DESC);
const showAdvancedFilters = ref(false);
// SiteUp: drawer aberto pelo (+) Atalhos → só Save shortcut + Cancel + ícone.
const filterDrawerShortcutMode = ref(false);
// chatsOnView is to store the chats that are currently visible on the screen,
// which mirrors the conversationList.
const chatsOnView = ref([]);
const showDeleteFoldersModal = ref(false);
// SiteUp: pasta alvo do delete a partir do painel Atalhos (null = pasta ativa).
const folderPendingDelete = ref(null);
const isContextMenuOpen = ref(false);
const appliedFilter = ref([]);
// SiteUp: etiquetas do filtro rápido do cabeçalho (multi-seleção, OU). Vive na sessão.
const quickFilterLabels = ref([]);
// SiteUp: busca estilo WhatsApp. O valor visível atualiza na hora; a consulta
// aplicada é debounced para não disparar uma requisição por tecla.
const conversationSearchInput = ref('');
const conversationSearchQuery = ref('');
// SiteUp: filtro binário de campanhas — UI removida (aba Automação isola
// campanhas/redes). Mantido em 'all'.
const campaignFilterMode = ref('all');
// SiteUp: filtro de grupos WhatsApp (contatos @g.us). 'all'=mostra tudo (padrão)
// | 'hide' (esconde grupos) | 'only' (só grupos). Persistido por usuário
// (uiSettings.conversation_group_filter); aplicado no backend (ConversationFinder).
const groupFilterMode = ref('all');
// SiteUp: canais sociais — UI removida (aba Automação). Sempre 'all'.
const socialFilterMode = ref('all');
const advancedFilterTypes = ref(
  advancedFilterOptions.map(filter => ({
    ...filter,
    attributeName: t(`FILTER.ATTRIBUTES.${filter.attributeI18nKey}`),
  }))
);

const currentUser = useMapGetter('getCurrentUser');
const chatLists = useMapGetter('getFilteredConversations');
const mineChatsList = useMapGetter('getMineChats');
const allChatList = useMapGetter('getAllStatusChats');
const unAssignedChatsList = useMapGetter('getUnAssignedChats');
const kanbanChatsList = useMapGetter('getKanbanChats');
const participatingChatsList = useMapGetter('getParticipatingChats');
const chatListLoading = useMapGetter('getChatListLoadingStatus');
const activeInbox = useMapGetter('getSelectedInbox');
const conversationStats = useMapGetter('conversationStats/getStats');
// SiteUp: contagens do meta (mine/all/unassigned) buscadas POR STATUS, pra cada
// chip de filtro rápido exibir a contagem do PRÓPRIO filtro. Sem isso, o
// all_count do status ativo "vazava" pro chip Todas (clicar Abertas mostrava 92
// no Todas em vez de no Abertas).
const statusCounts = ref({});
// SiteUp: contagem por pasta (custom view), key = folder.id, buscada pelo filtro salvo.
const folderCounts = ref({});
// SiteUp v654: contagem server-side por membership Kanban (key = preset key).
// null/undefined = sem fonte confiável → badge oculto (nunca zero falso).
const kanbanCounts = ref({});
const appliedFilters = useMapGetter('getAppliedConversationFiltersV2');
const folders = useMapGetter('customViews/getConversationCustomViews');
const agentList = useMapGetter('agents/getAgents');
const teamsList = useMapGetter('teams/getTeams');
const inboxesList = useMapGetter('inboxes/getInboxes');
const campaigns = useMapGetter('campaigns/getAllCampaigns');
const labels = useMapGetter('labels/getLabels');
const currentAccountId = useMapGetter('getCurrentAccountId');
// We can't useFunctionGetter here since it needs to be called on setup?
const getTeamFn = useMapGetter('teams/getTeam');
const getConversationById = useMapGetter('getConversationById');

useChatListKeyboardEvents(conversationListRef);
const {
  selectedConversations,
  selectedInboxes,
  selectConversation,
  deSelectConversation,
  selectAllConversations,
  resetBulkActions,
  isConversationSelected,
  onAssignAgent,
  onAssignLabels,
  onRemoveLabels,
  onAssignTeamsForBulk,
  onUpdateConversations,
  onTransferInbox,
} = useBulkActions();

const {
  initializeStatusAndAssigneeFilterToModal,
  initializeInboxTeamAndLabelFilterToModal,
} = useFilter({
  filteri18nKey: 'FILTER',
  attributeModel: 'conversation_attribute',
});

const { checkMissingAttributes } = useConversationRequiredAttributes();

// computed

const hasAppliedFilters = computed(() => {
  return appliedFilters.value.length !== 0;
});

const activeFolder = computed(() => {
  if (props.foldersId) {
    const activeView = folders.value.filter(
      view => view.id === Number(props.foldersId)
    );
    const [firstValue] = activeView;
    return firstValue;
  }
  return undefined;
});

const getContact = useMapGetter('contacts/getContact');
const folderContactId = useMapGetter('customViews/getActiveFolderContactId');

const activeFolderName = computed(() => {
  return activeFolder.value?.name;
});

const hasActiveFolders = computed(() => {
  return Boolean(activeFolder.value && props.foldersId !== 0);
});

const hasAppliedFiltersOrActiveFolders = computed(() => {
  return hasAppliedFilters.value || hasActiveFolders.value;
});

// SiteUp: uma visão personalizada continua sendo um item da navegação rápida.
// Ela não deve desmontar o rail nem esconder os controles normais da lista.
const showQuickFilterNavigation = computed(() => {
  return !hasAppliedFilters.value || hasActiveFolders.value;
});

const currentUserDetails = computed(() => {
  const { id, name } = currentUser.value;
  return { id, name };
});

const userPermissions = computed(() => {
  return getUserPermissions(currentUser.value, currentAccountId.value);
});

const isAdministrator = computed(
  () =>
    getUserRole(currentUser.value, currentAccountId.value) === 'administrator'
);

// SiteUp: config de personalização dos filtros rápidos (ordem + ocultos),
// persistida por usuário em uiSettings.
const arrayFromConfigValue = value => {
  if (Array.isArray(value)) return value;
  return value ? [value] : [];
};

const normalizedAdminAssigneeScope = value =>
  ADMIN_ASSIGNEE_SCOPE_VALUES.includes(value)
    ? value
    : ADMIN_ASSIGNEE_SCOPE.ALL;

// SiteUp: quick filters are per-account. Cross-account globalInbox IDs
// (e.g. Jan's inboxes while viewing account 11) empty the list otherwise.
const accountInboxIds = computed(() =>
  (inboxesList.value || []).map(inbox => Number(inbox.id)).filter(Boolean)
);

const quickFiltersRaw = computed(() =>
  readConversationsQuickFiltersRaw(
    uiSettings.value || {},
    currentAccountId.value
  )
);

// SiteUp v654: normalização de leitura (legado → canônico) sem backfill no DB.
const quickFilterConfig = computed(() =>
  normalizeConversationsQuickFilters(quickFiltersRaw.value, {
    validInboxIds: accountInboxIds.value,
  })
);

const persistQuickFilters = patch => {
  const accountId = currentAccountId.value;
  const key = conversationsQuickFiltersStorageKey(accountId);
  const previous = readConversationsQuickFiltersRaw(
    uiSettings.value || {},
    accountId
  );
  updateUISettings({
    [key]: {
      ...previous,
      ...patch,
    },
  });
};

// Active Kanban scope (built-in or duplicable preset) for request params / chips.
// Gate on the live assignee tab — not only persisted activeKey — so the first
// paint after clicking Kanban already sends/matches the correct funnel/stage
// before uiSettings.activeKey finishes merging.
const activeKanbanScope = computed(() => {
  if (activeAssigneeTab.value !== 'kanban') {
    return { funnelId: '', stageKey: '' };
  }
  const key = isKanbanPresetKey(quickFilterConfig.value.activeKey)
    ? quickFilterConfig.value.activeKey
    : 'kanban';
  if (key === 'kanban') {
    return {
      funnelId: quickFilterConfig.value.kanbanFunnel || '',
      stageKey: quickFilterConfig.value.kanbanStage || '',
    };
  }
  const preset = (quickFilterConfig.value.kanbanPresets || []).find(
    item => item.key === key
  );
  return {
    funnelId: preset?.funnelId || '',
    stageKey: preset?.stageKey || '',
  };
});

// SiteUp: ordem canônica dos status atômicos pra montar a string combinada.
const STATUS_ORDER = FILTER_STATUS_CHOICES.map(choice => choice.value);

// Status EFETIVO de um chip: para chips configuráveis (Minhas / Não atribuídas)
// é a combinação escolhida pelo usuário (ex.: "open,pending"); senão é o padrão
// do preset. Aceita override em array (multi) ou string (legado single-select).
const effectiveStatus = item => {
  if (!item.statusConfigurable) return item.status;
  const raw = quickFilterConfig.value.statuses[item.key];
  const arr = arrayFromConfigValue(raw);
  const ordered = STATUS_ORDER.filter(status => arr.includes(status));
  return ordered.length ? ordered.join(',') : item.status;
};

// SiteUp: funis (com etapas) pro seletor do filtro Kanban no Personalizar.
const funnels = useMapGetter('funnel/getFunnels');
const isKanbanEnabled = computed(() =>
  store.getters['accounts/isFeatureEnabledonAccount'](
    currentAccountId.value,
    'kanban_board'
  )
);

// Badge count for built-in presets: status meta when available; Kanban uses
// membership meta. Unknown → null (hide badge; never a false zero).
const resolvePresetBadgeCount = (item, status, countKey) => {
  if (countKey) {
    return resolveQuickFilterBadgeCount(
      statusCounts.value[`${status}|${item.conversationType || ''}`]?.[countKey]
    );
  }
  if (item.key === 'kanban') {
    return resolveQuickFilterBadgeCount(kanbanCounts.value[item.key]);
  }
  return null;
};

// SiteUp: todos os filtros permitidos (visíveis e ocultos), já ordenados pela
// preferência do usuário — alimenta o modo de personalização (drag-and-drop).
const allQuickFilterItems = computed(() => {
  const { order, hidden, kanbanPresets } = quickFilterConfig.value;
  const hasUserConfig = order.length > 0;
  // Visibilidade: ocultos explicitamente pelo usuário; itens já conhecidos
  // (em `order`) seguem o usuário; itens novos caem no defaultHidden do catálogo.
  const resolveHidden = item => {
    if (hidden.includes(item.key)) return true;
    if (hasUserConfig && order.includes(item.key)) return false;
    return Boolean(item.defaultHidden);
  };

  // 1. Presets fixos (respeita feature flag do Kanban + permissões)
  const itemsObject = {};
  QUICK_FILTERS.filter(
    preset => !preset.requiresKanban || isKanbanEnabled.value
  ).forEach(preset => {
    const tabPermission = ASSIGNEE_TYPE_TAB_PERMISSIONS[preset.assigneeType];
    itemsObject[preset.key] = {
      ...preset,
      permissions: tabPermission ? tabPermission.permissions : [...ROLES],
    };
  });
  const presetItems = filterItemsByPermission(
    itemsObject,
    userPermissions.value,
    item => item.permissions
  ).map(item => {
    // Status efetivo: combinação escolhida (configurável) ou o padrão do preset.
    const status = effectiveStatus(item);
    const countKey = getCountKeyForQuickFilter({
      preset: item,
      isAdministrator: isAdministrator.value,
    });
    return {
      ...item,
      type: 'preset',
      status,
      // SiteUp: ícone escolhido pelo usuário tem prioridade sobre o padrão.
      // F1.2e: escolha antiga (ui_settings) pode ser `i-lucide-*` — traduz na
      // leitura, sem migrar dado.
      icon: resolveSavedIconClass(
        quickFilterConfig.value.icons[item.key] || item.icon
      ),
      // SiteUp: presets podem ter nome fixo; senão traduz.
      name: item.name || t(`CHAT_LIST.QUICK_FILTERS.${item.i18nKey}`),
      // Contagem escopada pelo status efetivo + conversation_type do chip
      // (ex.: "open|unread", "open|scheduled", "open|" pros normais).
      count: resolvePresetBadgeCount(item, status, countKey),
      funnelId:
        item.key === 'kanban' ? quickFilterConfig.value.kanbanFunnel : '',
      stageKey:
        item.key === 'kanban' ? quickFilterConfig.value.kanbanStage : '',
      hidden: resolveHidden(item),
    };
  });

  // 2. SiteUp v654: presets Kanban duplicáveis (ui_settings, não pastas).
  const kanbanPresetItems = isKanbanEnabled.value
    ? (kanbanPresets || []).map(preset => ({
        key: preset.key,
        type: 'kanban_preset',
        assigneeType: 'kanban',
        status: wootConstants.STATUS_TYPE.ACTIVE,
        conversationType: '',
        countKey: null,
        icon: resolveSavedIconClass(
          quickFilterConfig.value.icons[preset.key] ||
            preset.icon ||
            'i-ri-kanban-view'
        ),
        name: preset.name || t('CHAT_LIST.QUICK_FILTERS.KANBAN'),
        // Server-side membership meta only — never hardcode false zero.
        count: resolveQuickFilterBadgeCount(kanbanCounts.value[preset.key]),
        funnelId: preset.funnelId || '',
        stageKey: preset.stageKey || '',
        defaultHidden: false,
        requiresKanban: true,
        permissions: ASSIGNEE_TYPE_TAB_PERMISSIONS.kanban?.permissions || [
          ...ROLES,
        ],
        hidden: resolveHidden({
          key: preset.key,
          defaultHidden: false,
        }),
      }))
    : [];

  // 3. Pastas (custom views de conversa) — ativadas por padrão; ficam depois
  // dos filtros globais na ordem canônica (normalizer coloca globals no início).
  const folderItems = (folders.value || []).map(folder => {
    const folderKey = `${FOLDER_FILTER_PREFIX}${folder.id}`;
    const item = {
      key: folderKey,
      type: 'folder',
      folderId: folder.id,
      // SiteUp: ícone escolhido pelo usuário; cai pro folder padrão se não houver.
      // F1.2e: escolha antiga pode ser `i-lucide-*` — traduz na leitura.
      icon: resolveSavedIconClass(
        quickFilterConfig.value.icons[folderKey] || 'i-ri-folder-line'
      ),
      name: folder.name,
      count: folderCounts.value[folder.id] ?? 0,
      defaultHidden: false,
    };
    return { ...item, hidden: resolveHidden(item) };
  });

  const list = [...presetItems, ...kanbanPresetItems, ...folderItems];
  if (!hasUserConfig) {
    return list;
  }
  return [...list].sort((a, b) => {
    const indexA = order.indexOf(a.key);
    const indexB = order.indexOf(b.key);
    return (
      (indexA === -1 ? Infinity : indexA) - (indexB === -1 ? Infinity : indexB)
    );
  });
});

// SiteUp: filtros visíveis na barra (exclui os ocultos pelo usuário)
const quickFilterItems = computed(() =>
  allQuickFilterItems.value.filter(item => !item.hidden)
);

// SiteUp: qual chip de filtro rápido está ativo, derivado do estado atual
const activeQuickFilterKey = computed(() => {
  if (props.foldersId) {
    return `${FOLDER_FILTER_PREFIX}${props.foldersId}`;
  }
  // Prefer the persisted key when it still matches the live list state
  // (needed for multiple Kanban presets that share assigneeType=kanban).
  const savedKey = quickFilterConfig.value.activeKey;
  if (
    savedKey &&
    isKanbanPresetKey(savedKey) &&
    activeAssigneeTab.value === 'kanban'
  ) {
    return savedKey;
  }
  const match = QUICK_FILTERS.find(preset => {
    const assigneeType = getAssigneeTypeForQuickFilter({
      preset,
      isAdministrator: isAdministrator.value,
    });
    return (
      assigneeType === activeAssigneeTab.value &&
      effectiveStatus(preset) === activeStatus.value &&
      (preset.conversationType || '') === (activeConversationType.value || '')
    );
  });
  return match ? match.key : savedKey || '';
});

const avatarRailFilterLabel = item => {
  // eslint-disable-next-line @intlify/vue-i18n/no-dynamic-keys
  const name = item.name || t(`CHAT_LIST.QUICK_FILTERS.${item.i18nKey}`);
  return item.count ? `${name} · ${item.count}` : name;
};

const avatarRailFilters = computed(() => {
  const byKey = new Map(
    (allQuickFilterItems.value || []).map(item => [item.key, item])
  );
  return PRIMARY_SEGMENT_KEYS.map(key => {
    const item = byKey.get(key);
    if (item) return item;
    const preset = QUICK_FILTERS.find(entry => entry.key === key);
    return {
      key,
      icon: preset?.icon || 'i-ri-filter-line',
      i18nKey: preset?.i18nKey || key.toUpperCase(),
      count: 0,
    };
  });
});

const showAssigneeInConversationCard = computed(() => {
  return (
    hasAppliedFiltersOrActiveFolders.value ||
    activeAssigneeTab.value === wootConstants.ASSIGNEE_TYPE.ALL
  );
});

const currentPageFilterKey = computed(() => {
  return hasAppliedFiltersOrActiveFolders.value
    ? 'appliedFilters'
    : activeAssigneeTab.value;
});

const inbox = useFunctionGetter('inboxes/getInbox', activeInbox);
const currentPage = useFunctionGetter(
  'conversationPage/getCurrentPageFilter',
  activeAssigneeTab
);
const currentFiltersPage = useFunctionGetter(
  'conversationPage/getCurrentPageFilter',
  currentPageFilterKey
);
const hasCurrentPageEndReached = useFunctionGetter(
  'conversationPage/getHasEndReached',
  currentPageFilterKey
);

const conversationCustomAttributes = useFunctionGetter(
  'attributes/getAttributesByModel',
  'conversation_attribute'
);

const conversationListPagination = computed(() => {
  const conversationsPerPage = 25;
  // eslint-disable-next-line no-use-before-define
  const activeTabCount = activeAssigneeTabCount.value;
  const visibleCount = chatsOnView.value?.length || 0;
  const isUnderPerPage =
    visibleCount < conversationsPerPage &&
    activeTabCount < conversationsPerPage &&
    activeTabCount > visibleCount;

  // Only force page 1 before the first successful fetch (currentPage === 0).
  // After page ≥ 1, never pin page back to 1: when a client refilter (Kanban /
  // Mine / Unassigned) shows fewer rows than the server membership count, the
  // old branch kept requesting page 1 forever and IntersectionObserver spun
  // the list spinner in a loop.
  if (
    !hasAppliedFiltersOrActiveFolders.value &&
    visibleCount === 0 &&
    isUnderPerPage &&
    currentPage.value === 0
  ) {
    return 1;
  }

  // Coerce missing/NaN page keys (e.g. legacy state without kanban) to 0.
  const pageBase = Number(currentPage.value);
  return (Number.isFinite(pageBase) ? pageBase : 0) + 1;
});

// SiteUp: caixas de entrada do filtro rápido ATIVO (quando a rota não é uma inbox
// específica). Restringe a lista a essas caixas (multi-select); vazio = todas.
// Tolera config legado singular (string/number) -> array. where(id: [..]) no
// backend já filtra por IN respeitando as caixas permitidas.
// SiteUp: caixa(s) global(is) aplicada(s) ao filtro ativo — vale pra qualquer chip.
const activeQuickFilterInboxIds = computed(() => {
  const raw = quickFilterConfig.value.globalInbox;
  return sanitizeGlobalInboxIds(
    arrayFromConfigValue(raw),
    accountInboxIds.value
  );
});

// SiteUp: ordenação leve (ícone ao lado de Etiquetas). Só 2 modos:
// mais recentes (last_activity desc) ↔ aguardando mais tempo (waiting_since asc).
const conversationViewMode = ref('last_activity_desc');

const sortRecentFirst = computed(
  () => conversationViewMode.value === 'last_activity_desc'
);

const activeConversationSortBy = computed(() => {
  return (
    CONVERSATION_VIEW_MODE_SORT_KEYS[conversationViewMode.value] ||
    wootConstants.SORT_BY_TYPE.LAST_ACTIVITY_AT_DESC
  );
});

const conversationFilters = computed(() => {
  const inboxId = getInboxIdForRequest({
    conversationInbox: props.conversationInbox,
    globalInbox: activeQuickFilterInboxIds.value,
    validInboxIds: accountInboxIds.value,
    searchQuery: conversationSearchQuery.value,
  });

  let selectedLabels;
  if (props.label) {
    selectedLabels = [props.label];
  } else if (quickFilterLabels.value.length) {
    selectedLabels = quickFilterLabels.value;
  }

  const kanbanScope =
    activeAssigneeTab.value === 'kanban' ? activeKanbanScope.value : null;

  return {
    inboxId,
    assigneeType: activeAssigneeTab.value,
    status: activeStatus.value,
    sortBy: activeConversationSortBy.value,
    page: conversationListPagination.value,
    labels: selectedLabels,
    teamId: props.teamId || undefined,
    conversationType: getConversationTypeForRequest({
      conversationType:
        props.conversationType || activeConversationType.value || undefined,
      searchQuery: conversationSearchQuery.value,
    }),
    campaignFilter: campaignFilterMode.value,
    groupFilter: groupFilterMode.value,
    socialFilter: socialFilterMode.value,
    adminAssigneeScope: isAdministrator.value
      ? quickFilterConfig.value.adminAssigneeScope
      : undefined,
    includeUnassignedInPendentes: normalizeIncludeUnassignedInPendentes(
      quickFilterConfig.value.includeUnassignedInPendentes
    ),
    // SiteUp v654: server-side Kanban membership (not client page filter).
    kanbanFunnelId: kanbanScope?.funnelId || undefined,
    kanbanStage: kanbanScope?.stageKey || undefined,
    q: conversationSearchQuery.value || undefined,
  };
});

const activeTeam = computed(() => {
  if (props.teamId) {
    return getTeamFn.value(props.teamId);
  }
  return {};
});

const pageTitle = computed(() => {
  if (hasAppliedFilters.value) {
    return t('CHAT_LIST.TAB_HEADING');
  }
  if (inbox.value.name) {
    return inbox.value.name;
  }
  if (activeTeam.value.name) {
    return activeTeam.value.name;
  }
  if (props.label) {
    return `#${props.label}`;
  }
  if (props.conversationType === wootConstants.CONVERSATION_TYPE.MENTION) {
    return t('CHAT_LIST.MENTION_HEADING');
  }
  if (
    props.conversationType === wootConstants.CONVERSATION_TYPE.PARTICIPATING
  ) {
    return t('CONVERSATION_PARTICIPANTS.SIDEBAR_MENU_TITLE');
  }
  if (props.conversationType === wootConstants.CONVERSATION_TYPE.UNATTENDED) {
    return t('CHAT_LIST.UNATTENDED_HEADING');
  }
  if (hasActiveFolders.value) {
    return activeFolder.value.name;
  }
  return t('CHAT_LIST.TAB_HEADING');
});

const assigneeTabItems = computed(() => {
  return filterItemsByPermission(
    ASSIGNEE_TYPE_TAB_PERMISSIONS,
    userPermissions.value,
    item => item.permissions
  )
    .filter(({ key }) => {
      // Verificar se é a aba kanban e se a feature flag está ativa
      if (key === 'kanban') {
        return isKanbanEnabled.value;
      }
      return true;
    })
    .map(({ key, count: countKey }) => ({
      key,
      name: t(`CHAT_LIST.ASSIGNEE_TYPE_TABS.${key}`),
      count:
        key === 'kanban'
          ? conversationStats.value[countKey] || 0
          : conversationStats.value[countKey] || 0,
    }));
});

// Pagination helper: trusted total for the active chip. Kanban uses
// membership meta (kanbanCounts), never legacy conversationStats (false 0).
const activeAssigneeTabCount = computed(() => {
  if (activeAssigneeTab.value === 'kanban') {
    const key = activeQuickFilterKey.value || 'kanban';
    const kanbanCount = resolveQuickFilterBadgeCount(kanbanCounts.value[key]);
    if (kanbanCount !== null) return kanbanCount;
    // Unknown membership total: use loaded length so under-per-page logic does
    // not treat a false zero as "all rows already loaded".
    return chatsOnView.value?.length || 0;
  }

  const tab = assigneeTabItems.value.find(
    item => item.key === activeAssigneeTab.value
  );
  return tab?.count ?? 0;
});

function filterByAssigneeTab(conversations) {
  // SiteUp v654: client refilter aligned with ConversationFinder membership.
  const scope = activeKanbanScope.value;
  return conversations.filter(conversation =>
    conversationMatchesAssigneeFilter(conversation, {
      assigneeType: activeAssigneeTab.value,
      currentUserId: currentUser.value?.id,
      kanbanFunnelId: scope.funnelId,
      kanbanStage: scope.stageKey,
    })
  );
}

function filterByAdminAssigneeScope(conversations) {
  if (!isAdministrator.value) return conversations;
  const adminScope = quickFilterConfig.value.adminAssigneeScope;
  const userId = currentUser.value?.id;
  return conversations.filter(conversation =>
    conversationMatchesAdminAssigneeScope(conversation, userId, adminScope, {
      isAdministrator: true,
    })
  );
}

const conversationList = computed(() => {
  let localConversationList = [];

  // SiteUp: busca (`q`) respeita os mesmos filtros da lista (status, assignee,
  // chips). O backend filtra + ordena; no cache do FE reaplicamos o recorte
  // assignee/kanban e a ordenação do toggle (mais recentes / aguardando).
  // SiteUp v654: me / unassigned / kanban refilter the shared cache (realtime
  // ADD/UPDATE/reassignment) using the same membership contract as the backend.
  if (!hasAppliedFiltersOrActiveFolders.value) {
    const filters = conversationFilters.value;
    if (
      props.conversationType === wootConstants.CONVERSATION_TYPE.PARTICIPATING
    ) {
      localConversationList = filterByAssigneeTab(
        participatingChatsList.value(filters)
      );
    } else if (activeAssigneeTab.value === 'me') {
      localConversationList = [...mineChatsList.value(filters)];
    } else if (activeAssigneeTab.value === 'unassigned') {
      localConversationList = [...unAssignedChatsList.value(filters)];
    } else if (activeAssigneeTab.value === 'kanban') {
      localConversationList = [
        ...kanbanChatsList.value(filters, activeKanbanScope.value),
      ];
    } else {
      localConversationList = [...allChatList.value(filters)];
    }
  } else {
    localConversationList = [...chatLists.value];
  }

  if (activeFolder.value) {
    const { payload } = activeFolder.value.query;
    localConversationList = localConversationList.filter(conversation => {
      return matchesFilters(conversation, payload);
    });
  }

  localConversationList = filterByAdminAssigneeScope(localConversationList);

  // Ordenação do toggle (mais recentes / aguardando mais tempo) também na busca.
  const comparator = conversationViewComparator(conversationViewMode.value);
  return [...localConversationList].sort(comparator);
});

// SiteUp V2 (tela 03): FIXADOS acima da FILA. Os fixados NÃO são
// virtualizados — são poucos por design (teto no composable) e precisam
// aparecer inteiros sem depender de scroll.
const { splitByPinned } = usePinnedConversations();
const pinnedConversations = computed(
  () => splitByPinned(conversationList.value).pinned
);
const queueConversations = computed(
  () => splitByPinned(conversationList.value).queue
);
// Cabeçalhos só existem quando há fixados — sem eles a lista fica igual à de
// hoje (aditivo, como o plano da tela 03 pede).
const hasPinnedSection = computed(() => pinnedConversations.value.length > 0);

const showEndOfListMessage = computed(() => {
  return (
    conversationList.value.length &&
    hasCurrentPageEndReached.value &&
    !chatListLoading.value
  );
});

// SiteUp: empty state da lista com contexto (nome do chip/pasta ativa) e saída
// de 1 clique — Limpar filtros (filtro avançado/pasta) ou Ver todas (chip).
const emptyStateContextName = computed(() => {
  if (conversationSearchQuery.value) return conversationSearchQuery.value;
  if (hasActiveFolders.value) return activeFolderName.value || '';
  if (hasAppliedFilters.value) return '';
  const active = allQuickFilterItems.value.find(
    item => item.key === activeQuickFilterKey.value
  );
  return active?.name || '';
});

const emptyStateTitle = computed(() => {
  return emptyStateContextName.value
    ? t('CHAT_LIST.EMPTY_STATE.TITLE_WITH_CONTEXT', {
        name: emptyStateContextName.value,
      })
    : t('CHAT_LIST.EMPTY_STATE.TITLE');
});

const emptyStateHint = computed(() => {
  return hasAppliedFiltersOrActiveFolders.value || conversationSearchQuery.value
    ? t('CHAT_LIST.EMPTY_STATE.HINT_FILTERED')
    : t('CHAT_LIST.EMPTY_STATE.HINT');
});

// Sem saída útil quando já está no chip "Todas" sem filtro/pasta ativa.
const showEmptyStateCta = computed(() => {
  return (
    hasAppliedFiltersOrActiveFolders.value ||
    conversationSearchQuery.value ||
    activeQuickFilterKey.value !== 'all'
  );
});

const emptyStateCtaLabel = computed(() => {
  return hasAppliedFiltersOrActiveFolders.value || conversationSearchQuery.value
    ? t('CHAT_LIST.EMPTY_STATE.CLEAR_FILTERS')
    : t('CHAT_LIST.EMPTY_STATE.VIEW_ALL');
});

function onEmptyStateCta() {
  if (conversationSearchQuery.value) {
    conversationSearchInput.value = '';
    conversationSearchQuery.value = '';
    // eslint-disable-next-line no-use-before-define
    resetAndFetchData();
    return;
  }

  if (hasActiveFolders.value) {
    // Pasta é rota própria — "limpar" = voltar pra lista normal de conversas
    // (o watcher de activeFolder refaz a busca).
    router.push({ name: 'home' });
    return;
  }
  if (hasAppliedFilters.value) {
    // eslint-disable-next-line no-use-before-define
    resetAndFetchData();
    return;
  }
  // eslint-disable-next-line no-use-before-define
  applyQuickFilter('all');
}

const allConversationsSelected = computed(() => {
  return (
    conversationList.value.length === selectedConversations.value.length &&
    conversationList.value.every(el =>
      selectedConversations.value.includes(el.id)
    )
  );
});

const uniqueInboxes = computed(() => {
  return [...new Set(selectedInboxes.value)];
});

// ---------------------- Methods -----------------------
function setFiltersFromUISettings() {
  const { conversations_filter_by: filterBy = {} } = uiSettings.value;
  const { status } = filterBy;
  activeStatus.value = status || wootConstants.STATUS_TYPE.OPEN;
  // SiteUp v5: ordenação acompanha o toggle do header (mais/menos recente).
  activeSortBy.value = activeConversationSortBy.value;
  // SiteUp: rocket active = hide; 'only' kept for explicit callers; invalid → all.
  // Campanha/social: UI saiu do painel Atalhos (Automação isola esses casos).
  campaignFilterMode.value = 'all';
  // SiteUp: restaura o filtro de grupos persistido (padrão 'all' = mostra tudo).
  const savedGroupFilter = uiSettings.value?.conversation_group_filter;
  groupFilterMode.value = ['hide', 'all', 'only'].includes(savedGroupFilter)
    ? savedGroupFilter
    : 'all';
  socialFilterMode.value = 'all';
}

function emitConversationLoaded() {
  emit('conversationLoad');
}

function adminAssigneeScopeForRequest() {
  return isAdministrator.value
    ? quickFilterConfig.value.adminAssigneeScope
    : undefined;
}

function includeUnassignedInPendentesForRequest() {
  return normalizeIncludeUnassignedInPendentes(
    quickFilterConfig.value.includeUnassignedInPendentes
  )
    ? undefined
    : false;
}

function fetchFilteredConversations(payload) {
  payload = useSnakeCase(payload);
  let page = currentFiltersPage.value + 1;
  store
    .dispatch('fetchFilteredConversations', {
      queryData: filterQueryGenerator(payload),
      page,
      sortBy: activeConversationSortBy.value,
      adminAssigneeScope: adminAssigneeScopeForRequest(),
    })
    .then(emitConversationLoaded);

  showAdvancedFilters.value = false;
}

function fetchSavedFilteredConversations(payload) {
  payload = normalizeSavedFilterQuery(useSnakeCase(payload));
  // Uma pasta corrompida não pode virar uma consulta sem critérios (que
  // mostraria todas as conversas). A lista já foi esvaziada pelo reset.
  if (!payload.payload.length) {
    // O sentinel fica visível em listas vazias. Marcar o fim evita novas
    // tentativas em loop para uma pasta que não tem consulta utilizável.
    store.dispatch('conversationPage/setEndReached', {
      filter: 'appliedFilters',
    });
    emitConversationLoaded();
    return;
  }
  let page = currentFiltersPage.value + 1;
  store
    .dispatch('fetchFilteredConversations', {
      queryData: payload,
      page,
      sortBy: activeConversationSortBy.value,
      adminAssigneeScope: adminAssigneeScopeForRequest(),
    })
    .then(emitConversationLoaded);
}

function onApplyFilter(payload) {
  payload = useSnakeCase(payload);
  resetBulkActions();
  store.dispatch('conversationPage/reset');
  store.dispatch('emptyAllConversations');
  fetchFilteredConversations(payload);
}

function closeAdvanceFiltersModal() {
  showAdvancedFilters.value = false;
  filterDrawerShortcutMode.value = false;
  appliedFilter.value = [];
}

async function onSaveFilter(payload, folderName, icon) {
  const payloadData = {
    name: folderName,
    filter_type: 0,
    query: filterQueryGenerator(useSnakeCase(payload)),
  };

  try {
    const response = await store.dispatch('customViews/create', payloadData);
    const folderId = response?.data?.id;
    const folderKey = folderId ? `${FOLDER_FILTER_PREFIX}${folderId}` : null;
    const iconClass =
      typeof icon === 'string' && icon.startsWith('i-')
        ? icon
        : 'i-ri-folder-line';

    // Appear in Atalhos already activated (visible on the icon bar).
    if (folderKey) {
      const previous = quickFiltersRaw.value || {};
      const prevHidden = Array.isArray(previous.hidden) ? previous.hidden : [];
      persistQuickFilters({
        hidden: prevHidden.filter(key => key !== folderKey),
        icons: {
          ...(previous.icons || {}),
          [folderKey]: iconClass,
        },
        activeKey: folderKey,
        order: [
          ...((previous.order || []).filter(key => key !== folderKey) || []),
          folderKey,
        ],
      });
    }

    closeAdvanceFiltersModal();
    useAlert(t('FILTER.CUSTOM_VIEWS.ADD.API_FOLDERS.SUCCESS_MESSAGE'));
    if (folderId) {
      router.push({
        name: 'folder_conversations',
        params: { id: folderId },
      });
    }
  } catch (error) {
    useAlert(
      error?.message || t('FILTER.CUSTOM_VIEWS.ADD.API_FOLDERS.ERROR_MESSAGE')
    );
  }
}

function onUpdateSavedFilter(payload, folderName) {
  const transformedPayload = useSnakeCase(payload);
  const payloadData = {
    ...unref(activeFolder),
    name: unref(folderName),
    query: filterQueryGenerator(transformedPayload),
  };
  store.dispatch('customViews/update', payloadData);
  closeAdvanceFiltersModal();
}

function onClickOpenDeleteFoldersModal() {
  folderPendingDelete.value = null;
  showDeleteFoldersModal.value = true;
}

function onDeleteCustomFilter(folderId) {
  const id = Number(folderId);
  const folder = (folders.value || []).find(view => Number(view.id) === id);
  if (!folder) return;
  folderPendingDelete.value = folder;
  showDeleteFoldersModal.value = true;
}

function onCloseDeleteFoldersModal() {
  showDeleteFoldersModal.value = false;
  folderPendingDelete.value = null;
}

const folderToDelete = computed(
  () => folderPendingDelete.value || activeFolder.value
);

const folderToDeleteId = computed(
  () => folderPendingDelete.value?.id || props.foldersId
);

function setParamsForEditFolderModal() {
  // Here we are setting the params for edit folder modal to show the existing values.

  // For agent, team, inboxes,and campaigns we get only the id's from the query.
  // So we are mapping the id's to the actual values.

  // For labels we get the name of the label from the query.
  // If we delete the label from the label list then we will not be able to show the label name.

  // For custom attributes we get only attribute key.
  // So we are mapping it to find the input type of the attribute to show in the edit folder modal.
  return {
    agents: agentList.value,
    teams: teamsList.value,
    inboxes: inboxesList.value,
    labels: labels.value,
    campaigns: campaigns.value,
    contacts: [getContact.value(folderContactId.value)],
    languages: languages,
    countries: countries,
    priority: [
      { id: 'low', name: t('CONVERSATION.PRIORITY.OPTIONS.LOW') },
      { id: 'medium', name: t('CONVERSATION.PRIORITY.OPTIONS.MEDIUM') },
      { id: 'high', name: t('CONVERSATION.PRIORITY.OPTIONS.HIGH') },
      { id: 'urgent', name: t('CONVERSATION.PRIORITY.OPTIONS.URGENT') },
    ],
    filterTypes: advancedFilterTypes.value,
    allCustomAttributes: conversationCustomAttributes.value,
  };
}

function initializeExistingFilterToModal() {
  const statusFilter = initializeStatusAndAssigneeFilterToModal(
    activeStatus.value,
    currentUserDetails.value,
    activeAssigneeTab.value
  );
  // TODO: Remove the usage of useCamelCase after migrating useFilter to camelcase
  if (statusFilter) {
    appliedFilter.value = [...appliedFilter.value, useCamelCase(statusFilter)];
  }

  // TODO: Remove the usage of useCamelCase after migrating useFilter to camelcase
  const otherFilters = initializeInboxTeamAndLabelFilterToModal(
    props.conversationInbox,
    inbox.value,
    props.teamId,
    activeTeam.value,
    props.label
  ).map(useCamelCase);

  appliedFilter.value = [...appliedFilter.value, ...otherFilters];
}

function initializeFolderToFilterModal(newActiveFolder) {
  // Here we are setting the params for edit folder modal.
  //  To show the existing values. when we click on edit folder button.

  // Here we get the query from the active folder.
  // And we are mapping the query to the actual values.
  // To show in the edit folder modal by the help of generateValuesForEditCustomViews helper.
  const query = unref(newActiveFolder)?.query?.payload;
  if (!Array.isArray(query)) return;

  const newFilters = query.map(filter => {
    const transformed = useCamelCase(filter);
    const values = Array.isArray(transformed.values)
      ? generateValuesForEditCustomViews(
          useSnakeCase(filter),
          setParamsForEditFolderModal()
        )
      : [];

    return {
      attributeKey: transformed.attributeKey,
      attributeModel: transformed.attributeModel,
      customAttributeType: transformed.customAttributeType,
      filterOperator: transformed.filterOperator,
      queryOperator: transformed.queryOperator ?? 'and',
      values,
    };
  });

  appliedFilter.value = [...appliedFilter.value, ...newFilters];
}

function initalizeAppliedFiltersToModal() {
  appliedFilter.value = [...appliedFilters.value];
}

function onToggleAdvanceFiltersModal() {
  if (showAdvancedFilters.value === true) {
    closeAdvanceFiltersModal();
    return;
  }

  filterDrawerShortcutMode.value = false;
  if (!hasAppliedFilters.value && !hasActiveFolders.value) {
    initializeExistingFilterToModal();
  }
  if (hasActiveFolders.value) {
    initializeFolderToFilterModal(activeFolder.value);
  }
  if (hasAppliedFilters.value) {
    initalizeAppliedFiltersToModal();
  }

  showAdvancedFilters.value = true;
}

function onCreateShortcutFilter() {
  if (showAdvancedFilters.value === true && filterDrawerShortcutMode.value) {
    closeAdvanceFiltersModal();
    return;
  }

  filterDrawerShortcutMode.value = true;
  appliedFilter.value = [];
  initializeExistingFilterToModal();
  showAdvancedFilters.value = true;
}

function fetchConversations() {
  store.dispatch('updateChatListFilters', conversationFilters.value);
  // SiteUp v654: Kanban also hits the server (membership + pagination).
  store.dispatch('fetchAllConversations').then(emitConversationLoaded);
}

function resetAndFetchData() {
  appliedFilter.value = [];
  resetBulkActions();
  store.dispatch('conversationPage/reset');
  store.dispatch('emptyAllConversations');
  store.dispatch('clearConversationFilters');
  if (hasActiveFolders.value) {
    const payload = activeFolder.value.query;
    fetchSavedFilteredConversations(payload);
  }
  if (props.foldersId) {
    return;
  }
  fetchConversations();
}

function setConversationSortRecent(recent) {
  const nextMode = recent ? 'last_activity_desc' : 'waiting';
  if (conversationViewMode.value === nextMode) return;
  conversationViewMode.value = nextMode;
  activeSortBy.value = activeConversationSortBy.value;
  store.dispatch('setChatSortFilter', activeSortBy.value);
  resetAndFetchData();
}

const applyConversationSearch = debounce(
  () => {
    conversationSearchQuery.value = conversationSearchInput.value.trim();
    resetAndFetchData();
  },
  350,
  false
);

function setConversationSearch(query) {
  conversationSearchInput.value = query;
  if (!query.trim()) {
    conversationSearchQuery.value = '';
    resetAndFetchData();
    return;
  }
  applyConversationSearch();
}

// SiteUp: aplica o filtro rápido de etiquetas (sessão) e re-busca a lista.
function setQuickFilterLabels(titles) {
  quickFilterLabels.value = Array.isArray(titles) ? titles : [];
  resetAndFetchData();
}

// SiteUp: aplica o filtro de grupos (@g.us) — persiste por usuário e re-busca lista +
// contadores (backend filtra por contact.identifier LIKE '%@g.us' → batem com a lista).
function setGroupFilter(mode) {
  groupFilterMode.value = ['hide', 'all', 'only'].includes(mode) ? mode : 'all';
  updateUISettings({ conversation_group_filter: groupFilterMode.value });
  resetAndFetchData();
  store.dispatch('conversationStats/get', conversationFilters.value);
  // eslint-disable-next-line no-use-before-define
  fetchStatusCounts();
}

function loadMoreConversations() {
  if (hasCurrentPageEndReached.value || chatListLoading.value) {
    return;
  }
  // Initial page comes from resetAndFetchData. If the visible list is still
  // empty after at least one page landed, do not keep auto-fetching — that was
  // the Kanban spinner loop when membership count > client-visible rows.
  if (!conversationList.value.length && currentPage.value >= 1) {
    return;
  }

  if (!hasAppliedFiltersOrActiveFolders.value) {
    fetchConversations();
  } else if (hasActiveFolders.value) {
    const payload = activeFolder.value.query;
    fetchSavedFilteredConversations(payload);
  } else if (hasAppliedFilters.value) {
    fetchFilteredConversations(appliedFilters.value);
  }
}

// Use IntersectionObserver instead of @scroll since Virtualizer only emits on user scroll.
// If the list doesn’t fill the viewport, loading can stall.
// IntersectionObserver triggers as soon as the sentinel is visible.
const intersectionObserverOptions = computed(() => ({
  root: conversationListRef.value,
  rootMargin: '100px 0px 100px 0px',
}));

// SiteUp: aplica um preset de filtro rápido (seta assignee + status + tipo)
function applyQuickFilter(key) {
  if (key === activeQuickFilterKey.value) {
    return;
  }
  const navigationTarget = getQuickFilterNavigationTarget({
    key,
    foldersId: props.foldersId,
  });
  // Pasta (custom view) → navega pra a rota da pasta (mesmas conversas da lateral)
  if (isFolderFilterKey(key)) {
    router.push(navigationTarget);
    return;
  }
  // Preset fixo ou Kanban duplicável → aplica o filtro inline (assignee + status + tipo)
  const preset = findQuickFilterByKey(
    key,
    quickFilterConfig.value.kanbanPresets
  );
  if (!preset) {
    return;
  }
  resetBulkActions();
  emitter.emit('clearSearchInput');
  activeAssigneeTab.value = getAssigneeTypeForQuickFilter({
    preset,
    isAdministrator: isAdministrator.value,
  });
  // SiteUp: respeita a combinação de status do usuário (chips configuráveis).
  activeStatus.value = effectiveStatus(preset);
  activeConversationType.value = preset.conversationType || '';
  // Sair primeiro da rota custom_view/:id. O watcher de activeFolder fará a
  // nova busca quando foldersId virar 0; buscar antes reaplicaria a pasta.
  if (navigationTarget) {
    router.push(navigationTarget);
  } else {
    resetAndFetchData();
  }
  // SiteUp: persiste o chip ativo (restaurado no mount) — só o delta, o merge é
  // da mutation/servidor (anti-clobber).
  persistQuickFilters({ activeKey: key });
}

watch(
  [conversationList, activeQuickFilterKey],
  () => {
    bindFocusConversationQueue({
      conversations: conversationList.value,
      applyFilter: applyQuickFilter,
      activeKey: activeQuickFilterKey.value,
    });
  },
  { immediate: true }
);

// SiteUp: na entrada, não pousar num chip vazio se outro visível tem conversa.
// Empty state continua existindo quando TODOS estão vazios. Só roda uma vez
// por montagem — clique manual num chip vazio depois disso é escolha do usuário.
const hasSettledFilterLanding = ref(false);
watch(
  () =>
    quickFilterItems.value.map(item => `${item.key}:${item.count}`).join('|'),
  () => {
    if (hasSettledFilterLanding.value || props.foldersId) return;
    const visible = quickFilterItems.value;
    if (!visible.length) return;
    const active = visible.find(
      item => item.key === activeQuickFilterKey.value
    );
    if (active && active.count == null) return;
    if (active && active.count > 0) {
      hasSettledFilterLanding.value = true;
      return;
    }
    const fallback = visible.find(item => Number(item.count) > 0);
    if (!fallback) {
      hasSettledFilterLanding.value = true;
      return;
    }
    hasSettledFilterLanding.value = true;
    applyQuickFilter(fallback.key);
  },
  { immediate: true }
);

// SiteUp: salva a personalização dos filtros rápidos (ordem + ocultos + kanban)
function onUpdateQuickFilterConfig({
  order,
  hidden,
  icons,
  showLabels,
  compact,
  statuses,
  globalInbox,
  kanbanFunnel,
  kanbanStage,
  kanbanPresets,
  adminAssigneeScope,
  includeUnassignedInPendentes,
  activeKey,
}) {
  const orderKeys = Array.isArray(order) ? order.map(String) : [];
  // Reset active chip when the active preset was removed (or order no longer has it).
  let nextActiveKey = 'unread';
  if (
    typeof activeKey === 'string' &&
    activeKey &&
    orderKeys.includes(activeKey)
  ) {
    nextActiveKey = activeKey;
  } else if (orderKeys.includes(quickFilterConfig.value.activeKey)) {
    nextActiveKey = quickFilterConfig.value.activeKey;
  }

  persistQuickFilters({
    order,
    hidden,
    icons,
    showLabels,
    compact,
    statuses,
    // SiteUp: caixa GLOBAL (única) — substitui o mapa `inboxes` por-filtro.
    // Sanitize so foreign-account inbox ids never persist for this account.
    globalInbox: sanitizeGlobalInboxIds(globalInbox, accountInboxIds.value),
    // SiteUp: escopo funil/etapa do built-in Kanban.
    kanbanFunnel,
    kanbanStage,
    // SiteUp v654: presets Kanban duplicáveis (por usuário).
    kanbanPresets: Array.isArray(kanbanPresets) ? kanbanPresets : [],
    // SiteUp: escopo global exclusivo para administradores.
    adminAssigneeScope: normalizedAdminAssigneeScope(adminAssigneeScope),
    includeUnassignedInPendentes: normalizeIncludeUnassignedInPendentes(
      includeUnassignedInPendentes ??
        quickFilterConfig.value.includeUnassignedInPendentes
    ),
    activeKey: nextActiveKey,
    builtinDefaultsVersion:
      quickFiltersRaw.value?.builtinDefaultsVersion ??
      SITEUP_BUILTIN_SHORTCUT_DEFAULTS_VERSION,
  });

  if (nextActiveKey !== activeQuickFilterKey.value) {
    applyQuickFilter(nextActiveKey);
  }
}

function openLastSavedItemInFolder() {
  const lastItemOfFolder = folders.value[folders.value.length - 1];
  const lastItemId = lastItemOfFolder.id;
  router.push({
    name: 'folder_conversations',
    params: { id: lastItemId },
  });
}

function openLastItemAfterDeleteInFolder() {
  folderPendingDelete.value = null;
  // Atalhos pode apagar pasta que não é a view ativa — só redireciona se a
  // rota atual apontava pra pasta que sumiu do store após o delete.
  if (!props.foldersId) return;
  const stillExists = (folders.value || []).some(
    view => Number(view.id) === Number(props.foldersId)
  );
  if (stillExists) return;
  if (folders.value.length > 0) {
    openLastSavedItemInFolder();
  } else {
    router.push({ name: 'home' });
    fetchConversations();
  }
}

function redirectToConversationList() {
  const {
    params: { accountId, inbox_id: inboxId, label, teamId },
    name,
  } = route;

  let conversationType = '';
  if (isOnMentionsView({ route: { name } })) {
    conversationType = wootConstants.CONVERSATION_TYPE.MENTION;
  } else if (isOnParticipatingView({ route: { name } })) {
    conversationType = wootConstants.CONVERSATION_TYPE.PARTICIPATING;
  } else if (isOnUnattendedView({ route: { name } })) {
    conversationType = wootConstants.CONVERSATION_TYPE.UNATTENDED;
  }
  router.push(
    conversationListPageURL({
      accountId,
      conversationType: conversationType,
      customViewId: props.foldersId,
      inboxId,
      label,
      teamId,
    })
  );
}

async function assignPriority(priority, conversationId = null) {
  store.dispatch('setCurrentChatPriority', {
    priority,
    conversationId,
  });
  store
    .dispatch('assignPriority', { conversationId, priority })
    .then(() => {
      useTrack(CONVERSATION_EVENTS.CHANGE_PRIORITY, {
        newValue: priority,
        from: 'Context menu',
      });
      useAlert(
        t('CONVERSATION.PRIORITY.CHANGE_PRIORITY.SUCCESSFUL', {
          // traduz o valor antes de interpolar — sem isso o toast saía "para urgent"
          priority: t(
            `CONVERSATION.PRIORITY.OPTIONS.${(priority || 'NONE').toUpperCase()}`
          ),
          conversationId,
        })
      );
    })
    .catch(() => {
      useAlert(t('CONVERSATION.PRIORITY.CHANGE_PRIORITY.FAILED'));
    });
}

async function markAsUnread(conversationId) {
  try {
    await store.dispatch('markMessagesUnread', {
      id: conversationId,
    });
    // SiteUp: atualiza o contador "Não lidas" na hora (antes só atualizava no
    // mount / em evento de websocket — o badge ficava stale após marcar).
    // eslint-disable-next-line no-use-before-define
    fetchStatusCounts();
    redirectToConversationList();
  } catch (error) {
    useAlert(t('CONVERSATION.CARD_CONTEXT_MENU.API.MARK_AS_UNREAD_FAILED'));
  }
}
async function markAsRead(conversationId) {
  try {
    await store.dispatch('markMessagesRead', {
      id: conversationId,
    });
    // eslint-disable-next-line no-use-before-define
    fetchStatusCounts();
  } catch (error) {
    useAlert(t('CONVERSATION.CARD_CONTEXT_MENU.API.MARK_AS_READ_FAILED'));
  }
}

async function onAssignTeam(team, conversationId = null) {
  try {
    await store.dispatch('assignTeam', {
      conversationId,
      teamId: team.id,
    });
    useAlert(
      t('CONVERSATION.CARD_CONTEXT_MENU.API.TEAM_ASSIGNMENT.SUCCESFUL', {
        team: team.name,
        conversationId,
      })
    );
  } catch (error) {
    useAlert(t('CONVERSATION.CARD_CONTEXT_MENU.API.TEAM_ASSIGNMENT.FAILED'));
  }
}

function toggleConversationStatus(
  conversationId,
  status,
  snoozedUntil,
  customAttributes = null
) {
  const payload = {
    conversationId,
    status,
    snoozedUntil,
  };

  if (customAttributes) {
    payload.customAttributes = customAttributes;
  }

  store
    .dispatch('toggleStatus', payload)
    .then(() => {
      useAlert(t('CONVERSATION.CHANGE_STATUS'));
    })
    .catch(() => {
      useAlert(t('CONVERSATION.CHANGE_STATUS_FAILED'));
    });
}

function handleResolveConversation(conversationId, status, snoozedUntil) {
  if (status !== wootConstants.STATUS_TYPE.RESOLVED) {
    toggleConversationStatus(conversationId, status, snoozedUntil);
    return;
  }

  // Check for required attributes before resolving
  const conversation = getConversationById.value(conversationId);
  const currentCustomAttributes = conversation?.custom_attributes || {};
  const { hasMissing, missing } = checkMissingAttributes(
    currentCustomAttributes
  );

  if (hasMissing) {
    // Pass conversation context through the modal's API
    const conversationContext = {
      id: conversationId,
      snoozedUntil,
    };
    resolveAttributesModalRef.value?.open(
      missing,
      currentCustomAttributes,
      conversationContext
    );
  } else {
    toggleConversationStatus(conversationId, status, snoozedUntil);
  }
}

function handleResolveWithAttributes({ attributes, context }) {
  if (context) {
    const existingConversation = getConversationById.value(context.id);
    const currentCustomAttributes =
      existingConversation?.custom_attributes || {};
    const mergedAttributes = { ...currentCustomAttributes, ...attributes };

    toggleConversationStatus(
      context.id,
      wootConstants.STATUS_TYPE.RESOLVED,
      context.snoozedUntil,
      mergedAttributes
    );
  }
}

function allSelectedConversationsStatus(status) {
  if (!selectedConversations.value.length) return false;
  return selectedConversations.value.every(item => {
    return getConversationById.value(item)?.status === status;
  });
}

function onContextMenuToggle(state) {
  isContextMenuOpen.value = state;
}

function toggleSelectAll(check) {
  selectAllConversations(check, conversationList);
}

// SiteUp: conta cada pasta (custom view) pelo filtro salvo, lendo all_count do
// endpoint /conversations/filter. Cosmético: ignora falhas de rede.
const fetchFolderCountsNow = async () => {
  await Promise.all(
    (folders.value || []).map(async folder => {
      const payload = folder?.query?.payload;
      if (!payload) return;
      try {
        const queryData = filterQueryGenerator(payload);
        const { data } = await ConversationApi.filter({
          queryData,
          page: 1,
          adminAssigneeScope: adminAssigneeScopeForRequest(),
        });
        const count = data?.data?.meta?.all_count ?? data?.meta?.all_count ?? 0;
        folderCounts.value = { ...folderCounts.value, [folder.id]: count };
      } catch (error) {
        // contador é cosmético — ignora falhas
      }
    })
  );
};

// SiteUp (fix contador x lista): a contagem do chip tem que usar o MESMO escopo de
// caixas que a lista (multi-caixa). Antes usava só props.conversationInbox e
// ignorava as caixas escolhidas no filtro -> contava a conta inteira e o número
// estourava acima da lista (ex.: 56 no chip x 1 na lista com 4 de 5 caixas).
// Rota de inbox específica > caixas escolhidas DAQUELE chip > todas.
// SiteUp: contadores usam a MESMA caixa global da lista (não mais por-filtro).
const presetInboxId = () => {
  if (props.conversationInbox) return props.conversationInbox;
  const raw = quickFilterConfig.value.globalInbox;
  const arr = arrayFromConfigValue(raw);
  const ids = arr.map(Number).filter(Boolean);
  return ids.length ? ids : undefined;
};

// SiteUp v654: badge Kanban via meta com assignee_type=kanban (membership
// server-side, pagination-safe). Falha → deixa null (oculta badge, sem zero falso).
// Aggregate into a local map then single assign — concurrent Promise.all must not
// spread a stale kanbanCounts snapshot and drop sibling keys.
const fetchKanbanCountsNow = async baseParams => {
  if (!isKanbanEnabled.value) return;

  const targets = [];
  const { hidden, kanbanPresets } = quickFilterConfig.value;
  const hiddenSet = new Set(hidden || []);

  // Built-in kanban chip when visible.
  if (!hiddenSet.has('kanban')) {
    targets.push({
      key: 'kanban',
      funnelId: quickFilterConfig.value.kanbanFunnel || '',
      stageKey: quickFilterConfig.value.kanbanStage || '',
    });
  }
  (kanbanPresets || []).forEach(preset => {
    if (hiddenSet.has(preset.key)) return;
    targets.push({
      key: preset.key,
      funnelId: preset.funnelId || '',
      stageKey: preset.stageKey || '',
    });
  });

  const results = await Promise.all(
    targets.map(async ({ key, funnelId, stageKey }) => {
      try {
        const {
          data: { meta },
        } = await ConversationApi.meta({
          ...baseParams,
          status: wootConstants.STATUS_TYPE.ACTIVE,
          assigneeType: 'kanban',
          kanbanFunnelId: funnelId || undefined,
          kanbanStage: stageKey || undefined,
        });
        return { key, count: Number(meta?.all_count) || 0 };
      } catch (error) {
        // Sem contagem confiável: não grava 0 falso (omit key from aggregate).
        return null;
      }
    })
  );

  const aggregated = aggregateKanbanBadgeCounts(results);
  if (Object.keys(aggregated).length) {
    kanbanCounts.value = { ...kanbanCounts.value, ...aggregated };
  }
};

// SiteUp A5: builds the preset list for the bundled /conversations/meta_bundle
// call — one entry per status|conversationType quick-filter chip plus one
// per Kanban funnel/stage preset, mirroring fetchStatusCountsNow's per-chip
// targets and fetchKanbanCountsNow's targets.
const buildMetaBundlePresets = () => {
  const presets = [];

  QUICK_FILTERS.filter(
    preset =>
      preset.countKey && (!preset.requiresKanban || isKanbanEnabled.value)
  ).forEach(preset => {
    const status = effectiveStatus(preset);
    const conversationType = preset.conversationType || '';
    presets.push({
      key: `preset:${status}|${conversationType}`,
      status,
      conversation_type: conversationType || undefined,
      inbox_id: presetInboxId(),
    });
  });

  if (isKanbanEnabled.value) {
    const { hidden, kanbanPresets } = quickFilterConfig.value;
    const hiddenSet = new Set(hidden || []);
    const kanbanTargets = [];
    if (!hiddenSet.has('kanban')) {
      kanbanTargets.push({
        key: 'kanban',
        funnelId: quickFilterConfig.value.kanbanFunnel || '',
        stageKey: quickFilterConfig.value.kanbanStage || '',
      });
    }
    (kanbanPresets || []).forEach(preset => {
      if (hiddenSet.has(preset.key)) return;
      kanbanTargets.push({
        key: preset.key,
        funnelId: preset.funnelId || '',
        stageKey: preset.stageKey || '',
      });
    });
    kanbanTargets.forEach(({ key, funnelId, stageKey }) => {
      presets.push({
        key: `kanban:${key}`,
        status: wootConstants.STATUS_TYPE.ACTIVE,
        assignee_type: 'kanban',
        kanban_funnel_id: funnelId || undefined,
        kanban_stage: stageKey || undefined,
        inbox_id: presetInboxId(),
      });
    });
  }

  return presets;
};

const applyMetaBundleResults = results => {
  const statusUpdates = {};
  const kanbanResults = [];
  (results || []).forEach(({ key, count }) => {
    if (key?.startsWith('preset:')) {
      statusUpdates[key.slice('preset:'.length)] = {
        mineCount: count?.mine_count || 0,
        allCount: count?.all_count || 0,
        unAssignedCount: count?.unassigned_count || 0,
      };
    } else if (key?.startsWith('kanban:')) {
      kanbanResults.push({
        key: key.slice('kanban:'.length),
        count: Number(count?.all_count) || 0,
      });
    }
  });

  if (Object.keys(statusUpdates).length) {
    statusCounts.value = { ...statusCounts.value, ...statusUpdates };
  }
  const aggregated = aggregateKanbanBadgeCounts(kanbanResults);
  if (Object.keys(aggregated).length) {
    kanbanCounts.value = { ...kanbanCounts.value, ...aggregated };
  }
};

// SiteUp A5: single round-trip for every chip count above; returns false on
// any failure (404 on an older backend, network error) so the caller falls
// back to the original per-chip Promise.all path unchanged.
const fetchMetaBundleNow = async baseParams => {
  const presets = buildMetaBundlePresets();
  if (!presets.length) return true;

  try {
    const {
      data: { meta_bundle: results },
    } = await ConversationApi.metaBundle({
      base: {
        labels: baseParams.labels,
        team_id: baseParams.teamId,
        campaign_filter: baseParams.campaignFilter,
        group_filter: baseParams.groupFilter,
        social_filter: baseParams.socialFilter,
        admin_assignee_scope: baseParams.adminAssigneeScope,
        include_unassigned_in_pendentes:
          includeUnassignedInPendentesForRequest(),
      },
      presets,
    });
    applyMetaBundleResults(results);
    return true;
  } catch (error) {
    return false;
  }
};

// SiteUp: busca o meta por COMBINAÇÃO status+conversation_type dos chips com
// contador, pra cada chip exibir a contagem do próprio filtro — inclusive
// Não lidas (open|unread) e Agendadas (open|scheduled), já que o meta respeita
// conversation_type. Também conta as pastas. Debounced.
const fetchStatusCountsNow = async () => {
  if (hasAppliedFiltersOrActiveFolders.value) return;
  const baseParams = {
    labels: props.label ? [props.label] : undefined,
    teamId: props.teamId || undefined,
    campaignFilter: campaignFilterMode.value,
    groupFilter: groupFilterMode.value,
    socialFilter: socialFilterMode.value,
    adminAssigneeScope: isAdministrator.value
      ? quickFilterConfig.value.adminAssigneeScope
      : undefined,
    includeUnassignedInPendentes: includeUnassignedInPendentesForRequest(),
  };

  const bundled = await fetchMetaBundleNow(baseParams);
  if (bundled) {
    await fetchFolderCountsNow();
    return;
  }

  // Fallback (bundle endpoint missing/failed): original per-chip requests.
  const presetTargets = {};
  QUICK_FILTERS.filter(
    preset =>
      preset.countKey && (!preset.requiresKanban || isKanbanEnabled.value)
  ).forEach(preset => {
    const status = effectiveStatus(preset);
    const conversationType = preset.conversationType || '';
    presetTargets[`${status}|${conversationType}`] = {
      status,
      conversationType,
      inboxId: presetInboxId(),
    };
  });

  await Promise.all([
    ...Object.entries(presetTargets).map(
      async ([key, { status, conversationType, inboxId }]) => {
        try {
          const {
            data: { meta },
          } = await ConversationApi.meta({
            ...baseParams,
            inboxId,
            status,
            conversationType: conversationType || undefined,
          });
          statusCounts.value = {
            ...statusCounts.value,
            [key]: {
              mineCount: meta?.mine_count || 0,
              allCount: meta?.all_count || 0,
              unAssignedCount: meta?.unassigned_count || 0,
            },
          };
        } catch (metaError) {
          // contador é cosmético — ignora falhas de rede
        }
      }
    ),
    fetchFolderCountsNow(),
    fetchKanbanCountsNow({
      ...baseParams,
      inboxId: presetInboxId(),
    }),
  ]);
};
const fetchStatusCounts = debounce(fetchStatusCountsNow, 800, false);

useEmitter('fetch_conversation_stats', () => {
  if (hasAppliedFiltersOrActiveFolders.value) return;
  store.dispatch('conversationStats/get', conversationFilters.value);
  fetchStatusCounts();
});

onMounted(() => {
  store.dispatch('setChatListFilters', conversationFilters.value);
  setFiltersFromUISettings();
  // SiteUp: one-shot prod migration — split Não lidas/Pendentes and reset
  // builtin shortcuts without wiping folders / custom views / presets.
  if (quickFilterConfig.value.needsBuiltinDefaultsPersist) {
    persistQuickFilters({
      order: quickFilterConfig.value.order,
      hidden: quickFilterConfig.value.hidden,
      kanbanPresets: quickFilterConfig.value.kanbanPresets,
      activeKey: quickFilterConfig.value.activeKey,
      builtinDefaultsVersion: SITEUP_BUILTIN_SHORTCUT_DEFAULTS_VERSION,
    });
  }
  // Self-heal: drop foreign-account inbox ids and migrate legacy unscoped
  // config into conversations_quick_filters_<accountId>.
  const rawGlobalInbox = sanitizeGlobalInboxIds(
    quickFiltersRaw.value?.globalInbox,
    null
  );
  const cleanGlobalInbox = quickFilterConfig.value.globalInbox || [];
  const scopedKey = conversationsQuickFiltersStorageKey(currentAccountId.value);
  const missingScopedKey = !uiSettings.value?.[scopedKey];
  const inboxScopeDirty =
    rawGlobalInbox.length !== cleanGlobalInbox.length ||
    rawGlobalInbox.some(
      (id, idx) => Number(id) !== Number(cleanGlobalInbox[idx])
    );
  if (missingScopedKey || inboxScopeDirty) {
    const { needsBuiltinDefaultsPersist: _omitPersistFlag, ...persistable } =
      quickFilterConfig.value;
    persistQuickFilters({
      ...persistable,
      globalInbox: cleanGlobalInbox,
    });
  }
  // SiteUp: restaura o chip de filtro rápido ativo (persistido por usuário) —
  // sem isto, quem trabalha em "Não lidas" refazia o clique a cada F5. Não vale
  // pra pastas (rota própria) nem em rota de pasta.
  const savedKey = quickFilterConfig.value.activeKey;
  const savedPreset =
    savedKey && !isFolderFilterKey(savedKey)
      ? findQuickFilterByKey(savedKey, quickFilterConfig.value.kanbanPresets)
      : null;
  if (savedPreset && !props.foldersId) {
    activeAssigneeTab.value = getAssigneeTypeForQuickFilter({
      preset: savedPreset,
      isAdministrator: isAdministrator.value,
    });
    activeStatus.value = effectiveStatus(savedPreset);
    activeConversationType.value = savedPreset.conversationType || '';
  }
  store.dispatch('setChatStatusFilter', activeStatus.value);
  store.dispatch('setChatSortFilter', activeSortBy.value);
  resetAndFetchData();
  fetchStatusCounts();
  if (hasActiveFolders.value) {
    store.dispatch('campaigns/get');
  }
  // SiteUp: carrega os funis pro seletor de funil/etapa do filtro Kanban.
  if (isKanbanEnabled.value) {
    store.dispatch('funnel/fetch').catch(() => {});
  }
});

const deleteConversationDialogRef = ref(null);
const selectedConversationId = ref(null);

async function deleteConversation() {
  try {
    await store.dispatch('deleteConversation', selectedConversationId.value);
    redirectToConversationList();
    selectedConversationId.value = null;
    deleteConversationDialogRef.value.close();
    useAlert(t('CONVERSATION.SUCCESS_DELETE_CONVERSATION'));
  } catch (error) {
    useAlert(t('CONVERSATION.FAIL_DELETE_CONVERSATION'));
  }
}

const handleDelete = conversationId => {
  selectedConversationId.value = conversationId;
  deleteConversationDialogRef.value.open();
};

provide('selectConversation', selectConversation);
provide('deSelectConversation', deSelectConversation);
provide('assignAgent', onAssignAgent);
provide('assignTeam', onAssignTeam);
provide('assignLabels', onAssignLabels);
provide('transferInbox', onTransferInbox);
provide('removeLabels', onRemoveLabels);
provide('updateConversationStatus', handleResolveConversation);
provide('toggleContextMenu', onContextMenuToggle);
provide('markAsUnread', markAsUnread);
provide('markAsRead', markAsRead);
provide('assignPriority', assignPriority);
provide('isConversationSelected', isConversationSelected);
provide('deleteConversation', handleDelete);

// SiteUp (fix contador stale): ao mudar o CONTEXTO (inbox/label/team/folder/tipo),
// além de refazer a lista, é preciso re-buscar os contadores dos chips de filtro
// rápido (statusCounts). Sem isto, resetAndFetchData só atualizava a lista e os
// tabs (conversationStats vem no meta da lista) — os chips (Todos/Não lidas/
// Agendadas) ficavam PRESOS na contagem do inbox anterior. fetchStatusCounts é
// debounced (800ms) e ignora quando há filtro/folder ativo, então é barato.
const resetForContextChange = () => {
  resetAndFetchData();
  fetchStatusCounts();
};

// SiteUp: quando a caixa GLOBAL, escopo admin ou funil/etapa Kanban mudam,
// lista e contadores (badge) precisam ser buscados com o mesmo escopo.
// Sem isto, trocar a etapa no dropdown mantinha o badge do funil inteiro.
watch(
  () =>
    JSON.stringify({
      globalInbox: quickFilterConfig.value.globalInbox || [],
      adminAssigneeScope: isAdministrator.value
        ? quickFilterConfig.value.adminAssigneeScope
        : ADMIN_ASSIGNEE_SCOPE.ALL,
      includeUnassignedInPendentes: normalizeIncludeUnassignedInPendentes(
        quickFilterConfig.value.includeUnassignedInPendentes
      ),
      kanbanFunnel: quickFilterConfig.value.kanbanFunnel || '',
      kanbanStage: quickFilterConfig.value.kanbanStage || '',
      kanbanPresets: quickFilterConfig.value.kanbanPresets || [],
    }),
  () => resetForContextChange()
);

watch(activeTeam, () => resetForContextChange());

watch(
  computed(() => props.conversationInbox),
  () => resetForContextChange()
);
watch(
  computed(() => props.label),
  () => resetForContextChange()
);
watch(
  computed(() => props.conversationType),
  () => resetForContextChange()
);

watch(activeFolder, (newVal, oldVal) => {
  if (newVal !== oldVal) {
    store.dispatch('customViews/setActiveConversationFolder', newVal || null);
  }
  resetForContextChange();
});

// Mirror the *visible* list (post assignee/Kanban refilter), not only store
// chatLists — otherwise tab switches leave chatsOnView stale and pagination
// thinks the viewport is still full / empty incorrectly.
watch(
  conversationList,
  list => {
    chatsOnView.value = list;
  },
  { immediate: true }
);

watch(conversationFilters, (newVal, oldVal) => {
  if (newVal !== oldVal) {
    store.dispatch('updateChatListFilters', newVal);
  }
});
</script>

<template>
  <div
    class="flex flex-col flex-shrink-0 min-w-0 overflow-hidden conversations-list-wrap bg-n-surface-1 relative"
    :class="[
      { hidden: !showConversationList },
      isOnExpandedLayout
        ? 'basis-full w-full'
        : listDragWidth != null
          ? ''
          : isAvatarRail
            ? 'w-16'
            : isListCompact
              ? 'w-64'
              : 'w-[21.25rem] xl:w-[24rem] 2xl:w-[25rem]',
    ]"
    :style="
      !isOnExpandedLayout && listDragWidth != null
        ? { width: `${listDragWidth}px` }
        : undefined
    "
  >
    <div
      v-if="!isOnExpandedLayout"
      class="hidden md:block absolute top-0 h-full w-2 cursor-col-resize z-30 ltr:right-0 rtl:left-0 group"
      @mousedown="startListDrag"
      @touchstart="startListDrag"
      @dblclick="toggleListCompact"
    >
      <div
        class="absolute top-0 h-full w-px ltr:right-0 rtl:left-0 bg-transparent group-hover:bg-n-brand transition-colors"
        :class="{ 'bg-n-brand': listDragWidth != null }"
      />
    </div>
    <slot />
    <div
      data-testid="conversation-list-shell"
      class="grid min-h-0 min-w-0 flex-1 grid-cols-1 grid-rows-[minmax(0,1fr)] overflow-hidden rounded-xl border border-n-weak bg-n-surface-1"
      :class="isAvatarRail ? 'm-1' : 'm-2 pt-[env(safe-area-inset-top,0px)]'"
    >
      <!-- Coluna principal da lista: header + pesquisa + visão + conversas -->
      <div
        class="relative col-start-1 row-start-1 flex min-h-0 min-w-0 flex-col overflow-hidden"
      >
        <!-- Alvos internos para menus (nunca body / sidebar global) -->
        <div
          id="conversation-quick-filter-customizer-target"
          class="pointer-events-none absolute inset-0 z-30 overflow-hidden"
        />

        <ChatListHeader
          v-if="!isAvatarRail"
          :page-title="pageTitle"
          :has-applied-filters="hasAppliedFilters"
          :has-active-folders="hasActiveFolders"
          :active-status="activeStatus"
          :conversation-stats="conversationStats"
          :is-list-loading="chatListLoading && !conversationList.length"
          :search-query="conversationSearchInput"
          :filter-active="isChromeFilterActive"
          :filter-open="isChromeFilterOpen"
          :shortcuts-open="isChromeShortcutsOpen"
          :is-list-compact="isListCompact"
          :is-focus-mode="isFocusModeEnabled"
          @toggle-compact="toggleListCompact"
          @delete-folders="onClickOpenDeleteFoldersModal"
          @filters-modal="onToggleAdvanceFiltersModal"
          @reset-filters="resetAndFetchData"
          @update:search-query="setConversationSearch"
          @toggle-filter="
            rect => desktopQuickFilterTabsRef?.toggleFilterPanel(rect)
          "
          @toggle-shortcuts="
            rect => desktopQuickFilterTabsRef?.toggleMorePanel(rect)
          "
        />

        <div
          id="conversation-label-filter-panel-target"
          class="empty:hidden min-w-0 px-3 pb-2"
        />
        <div
          v-if="isAvatarRail && showQuickFilterNavigation"
          class="flex flex-col items-center gap-0.5 border-b border-n-weak py-1.5"
          role="tablist"
          :aria-label="$t('CHAT_LIST.QUICK_FILTERS.FILTERS_SECTION')"
        >
          <button
            v-for="item in avatarRailFilters"
            :key="item.key"
            v-tooltip.right="avatarRailFilterLabel(item)"
            type="button"
            role="tab"
            :aria-selected="item.key === activeQuickFilterKey"
            :aria-label="avatarRailFilterLabel(item)"
            class="relative flex size-8 items-center justify-center rounded-full transition-colors"
            :class="
              item.key === activeQuickFilterKey
                ? 'bg-n-blue-9/15 text-n-blue-11'
                : 'text-n-slate-11 hover:bg-n-alpha-2 hover:text-n-slate-12'
            "
            @click="applyQuickFilter(item.key)"
          >
            <span class="size-4" :class="[item.icon]" />
            <span
              v-if="item.count"
              class="absolute right-0.5 top-0.5 size-1.5 rounded-full bg-n-blue-9"
              aria-hidden="true"
            />
          </button>
        </div>
        <!-- SiteUp v5: segmento + ícones de atalho + painel Atalhos. -->
        <QuickFilterTabs
          v-if="showQuickFilterNavigation && !isAvatarRail"
          ref="desktopQuickFilterTabsRef"
          orientation="horizontal"
          teleport-target="#conversation-quick-filter-customizer-target"
          :items="quickFilterItems"
          :all-items="allQuickFilterItems"
          :active-key="activeQuickFilterKey"
          :show-labels="!isListCompact"
          :dense="isListCompact"
          :icon-overrides="quickFilterConfig.icons"
          :status-overrides="quickFilterConfig.statuses"
          :global-inbox="quickFilterConfig.globalInbox"
          :inboxes="inboxesList"
          :funnels="funnels"
          :kanban-funnel="quickFilterConfig.kanbanFunnel"
          :kanban-stage="quickFilterConfig.kanbanStage"
          :kanban-presets="quickFilterConfig.kanbanPresets"
          :compact="quickFilterConfig.compact"
          :admin-assignee-scope="quickFilterConfig.adminAssigneeScope"
          :include-unassigned-in-pendentes="
            quickFilterConfig.includeUnassignedInPendentes
          "
          :can-configure-admin-assignee-scope="isAdministrator"
          :group-filter="groupFilterMode"
          :sort-recent-first="sortRecentFirst"
          @select="applyQuickFilter"
          @update-config="onUpdateQuickFilterConfig"
          @create-filter="onCreateShortcutFilter"
          @delete-custom-filter="onDeleteCustomFilter"
          @update:group-filter="setGroupFilter"
          @set-sort-recent="setConversationSortRecent"
        >
          <template #label-filter>
            <ConversationLabelQuickFilter
              v-if="!label"
              :labels="labels"
              :model-value="quickFilterLabels"
              variant="row"
              teleport-target="#conversation-label-filter-panel-target"
              @update:model-value="setQuickFilterLabels"
            />
          </template>
        </QuickFilterTabs>
        <div
          id="conversationFilterTeleportTarget"
          class="empty:hidden min-h-0 min-w-0 flex-1 px-3 pb-2 [&>div]:flex [&>div]:h-full [&>div]:min-h-0 [&>div]:min-w-0 [&>div]:w-full"
        />

        <DeleteCustomViews
          v-if="showDeleteFoldersModal"
          v-model:show="showDeleteFoldersModal"
          :active-custom-view="folderToDelete"
          :custom-views-id="folderToDeleteId"
          :open-last-item-after-delete="openLastItemAfterDeleteInFolder"
          @close="onCloseDeleteFoldersModal"
        />

        <!-- SiteUp: empty state com contexto e saída de 1 clique (Limpar filtros / Ver todas) -->
        <div
          v-if="
            !showAdvancedFilters && !chatListLoading && !conversationList.length
          "
          class="flex items-start justify-center"
          :class="isAvatarRail ? 'overflow-hidden p-1.5' : 'overflow-auto p-4'"
        >
          <button
            v-if="isAvatarRail"
            v-tooltip.right="emptyStateTitle"
            type="button"
            class="flex size-9 shrink-0 items-center justify-center rounded-xl border border-dashed border-n-weak text-n-slate-11 hover:bg-n-alpha-2"
            :aria-label="emptyStateTitle"
            @click="showEmptyStateCta ? onEmptyStateCta() : undefined"
          >
            <span class="i-ri-inbox-line size-4" />
          </button>
          <PanelEmptyState
            v-else
            class="w-full"
            icon="i-ri-inbox-line"
            :title="emptyStateTitle"
            :hint="emptyStateHint"
          >
            <template v-if="showEmptyStateCta" #cta>
              <Button
                sm
                faded
                slate
                :label="emptyStateCtaLabel"
                @click="onEmptyStateCta"
              />
            </template>
          </PanelEmptyState>
        </div>
        <ConversationBulkActions
          v-if="!showAdvancedFilters && selectedConversations.length"
          :conversations="selectedConversations"
          :all-conversations-selected="allConversationsSelected"
          :selected-inboxes="uniqueInboxes"
          :show-open-action="allSelectedConversationsStatus('open')"
          :show-resolved-action="allSelectedConversationsStatus('resolved')"
          :show-snoozed-action="allSelectedConversationsStatus('snoozed')"
          @select-all-conversations="toggleSelectAll"
          @assign-agent="onAssignAgent"
          @update-conversations="onUpdateConversations"
          @assign-labels="onAssignLabels"
          @assign-team="onAssignTeamsForBulk"
          @transfer-inbox="onTransferInbox"
        />
        <div
          v-show="!showAdvancedFilters"
          ref="conversationListRef"
          class="conversations-list min-h-0 flex-1 overflow-y-auto"
          :class="{ '!overflow-hidden': isContextMenuOpen }"
        >
          <template v-if="hasPinnedSection">
            <div
              v-if="!isAvatarRail"
              data-testid="chat-list-section-pinned"
              class="flex items-center gap-1.5 px-4 pb-1 pt-2.5 text-[11.5px] font-bold uppercase tracking-[0.6px] text-n-slate-11"
            >
              <span class="i-ri-pushpin-line size-3.5 shrink-0" />
              {{ $t('CHAT_LIST.SECTIONS.PINNED') }}
            </div>
            <ConversationItem
              v-for="(item, index) in pinnedConversations"
              :key="`pinned-${item.id}`"
              :source="item"
              :label="label"
              :team-id="teamId"
              :folders-id="foldersId"
              :conversation-type="conversationType"
              :show-assignee="showAssigneeInConversationCard"
              :data-index="index"
              @select-conversation="selectConversation"
              @de-select-conversation="deSelectConversation"
            />
            <div
              v-if="!isAvatarRail"
              data-testid="chat-list-section-queue"
              class="px-4 pb-1 pt-3 text-[11.5px] font-bold uppercase tracking-[0.6px] text-n-slate-11"
            >
              {{ $t('CHAT_LIST.SECTIONS.QUEUE') }}
            </div>
          </template>
          <Virtualizer
            ref="virtualListRef"
            v-slot="{ item, index }"
            :data="queueConversations"
          >
            <ConversationItem
              :source="item"
              :label="label"
              :team-id="teamId"
              :folders-id="foldersId"
              :conversation-type="conversationType"
              :show-assignee="showAssigneeInConversationCard"
              :data-index="index"
              @select-conversation="selectConversation"
              @de-select-conversation="deSelectConversation"
            />
          </Virtualizer>
          <div v-if="chatListLoading" class="my-4 flex justify-center">
            <Spinner class="text-n-brand" />
          </div>
          <p
            v-else-if="showEndOfListMessage && !isAvatarRail"
            class="p-4 text-center text-n-slate-11"
          >
            {{ $t('CHAT_LIST.EOF') }}
          </p>
          <!-- Only observe when rows are visible. An empty list + always-on
               sentinel was re-triggering fetch in a spinner loop (Kanban). -->
          <IntersectionObserver
            v-else-if="conversationList.length"
            :options="intersectionObserverOptions"
            @observed="loadMoreConversations"
          />
        </div>
      </div>
    </div>
    <Dialog
      ref="deleteConversationDialogRef"
      type="alert"
      :title="
        $t('CONVERSATION.DELETE_CONVERSATION.TITLE', {
          conversationId: selectedConversationId,
        })
      "
      :description="$t('CONVERSATION.DELETE_CONVERSATION.DESCRIPTION')"
      :confirm-button-label="$t('CONVERSATION.DELETE_CONVERSATION.CONFIRM')"
      @confirm="deleteConversation"
      @close="selectedConversationId = null"
    />
    <TeleportWithDirection
      v-if="showAdvancedFilters"
      to="#conversationFilterTeleportTarget"
    >
      <ConversationFilter
        v-model="appliedFilter"
        contained
        :folder-name="activeFolderName"
        :is-folder-view="hasActiveFolders && !filterDrawerShortcutMode"
        :shortcut-mode="filterDrawerShortcutMode"
        @apply-filter="onApplyFilter"
        @save-filter="onSaveFilter"
        @update-folder="onUpdateSavedFilter"
        @close="closeAdvanceFiltersModal"
      />
    </TeleportWithDirection>
    <ConversationResolveAttributesModal
      ref="resolveAttributesModalRef"
      @submit="handleResolveWithAttributes"
    />
  </div>
</template>
